import { useEffect, useState } from "react";
import {
  BadgeCheck,
  CalendarClock,
  Download,
  Hash,
  PhoneCall,
  ShieldCheck,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  QuoteCovers,
  QuoteDisclaimer,
  QuoteTotals,
} from "@/components/quote/QuoteBreakdown";
import { api, messageFrom } from "@/lib/api";
import { formatDate } from "@/lib/format";

/** Calm count-up on the headline figure. Skipped for reduced-motion users. */
function useCountUp(target, duration = 750) {
  const [value, setValue] = useState(target);

  useEffect(() => {
    if (target === null || target === undefined) return undefined;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setValue(target);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const tick = (nowTs) => {
      const progress = Math.min(1, (nowTs - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
      else setValue(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);

  return value;
}

const NEXT_STEPS = [
  {
    choice: "email_quote",
    label: "Send this quote to my inbox",
    hint: "We'll make sure a copy lands in your email",
    icon: Mail,
    testId: "quote-email-preference-button",
  },
  {
    choice: "call_me",
    label: "Have someone call me",
    hint: "We'll ring you at the time you gave us",
    icon: PhoneCall,
    testId: "quote-call-me-button",
  },
];

export const QuoteReveal = ({ result, onStartOver }) => {
  const quote = result?.quote || {};
  const emphasisTotal = (quote.totals || []).find((t) => t.emphasis);
  const animated = useCountUp(emphasisTotal ? emphasisTotal.amount : null);
  const [choice, setChoice] = useState(null);
  const [savingChoice, setSavingChoice] = useState(null);

  const pdfUrl = api.quotePdfUrl(result.quote_ref);
  const emailed = Boolean(result?.email?.sent);
  const emailedTo = result?.email?.to || result?.contact?.email;

  const pickNextStep = async (nextChoice) => {
    setSavingChoice(nextChoice);
    try {
      await api.nextStep(result.quote_ref, nextChoice);
      setChoice(nextChoice);
      toast.success("Thanks — we've noted that and our team will pick it up.");
    } catch (error) {
      toast.error(messageFrom(error));
    } finally {
      setSavingChoice(null);
    }
  };

  return (
    <div data-testid="quote-reveal" className="tt-fade-in space-y-6">
      {/* ---- header ---- */}
      <div className="tt-card tt-ruled overflow-hidden">
        <div className="px-5 py-6 sm:px-7 sm:py-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="tt-stamp" data-testid="quote-ready-stamp">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Quote ready
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              Prepared for {result.contact?.full_name} · {result.contact?.company}
            </span>
          </div>

          <h2
            data-testid="quote-headline"
            className="mt-4 text-2xl font-bold leading-tight text-foreground sm:text-[32px]"
          >
            Your quote: {quote.headline}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {quote.service_label}
            {quote.entity_label ? ` · ${quote.entity_label}` : ""}
          </p>

          <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-white px-4 py-3">
              <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                <Hash className="h-3 w-3" aria-hidden="true" />
                Quote reference
              </dt>
              <dd
                data-testid="quote-reference"
                className="mt-1 font-amount text-sm font-bold text-foreground"
              >
                {result.quote_ref}
              </dd>
            </div>
            <div className="rounded-lg border border-border bg-white px-4 py-3">
              <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                <CalendarClock className="h-3 w-3" aria-hidden="true" />
                Date issued
              </dt>
              <dd className="mt-1 text-sm font-bold text-foreground">
                {formatDate(result.issued_at)}
              </dd>
            </div>
            <div className="rounded-lg border border-border bg-white px-4 py-3">
              <dt className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary">
                <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                Valid until
              </dt>
              <dd data-testid="quote-valid-until" className="mt-1 text-sm font-bold text-foreground">
                {formatDate(result.valid_until)}
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  ({result.validity_days} days)
                </span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ---- the numbers ---- */}
      <QuoteTotals totals={quote.totals} animatedValue={animated} />

      <div>
        <h3 className="tt-eyebrow">What your quote covers</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          One figure for the whole engagement, based on the answers you gave us. Everything
          listed below is included — nothing here is billed separately.
        </p>
        <div className="mt-4">
          <QuoteCovers covers={quote.covers} />
        </div>
      </div>

      <QuoteDisclaimer disclaimer={result.disclaimer} notices={quote.notices} />

      {/* ---- actions ---- */}
      <div className="tt-card p-5 sm:p-6">
        <h3 className="text-base font-bold text-foreground">Take your quote with you</h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          Your PDF is generated on our server from the figures we stored, and carries a QR code
          anyone can scan to check it against our records.
        </p>
        {emailed ? (
          <p
            data-testid="quote-email-sent-notice"
            className="mt-4 flex items-start gap-2 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]"
          >
            <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              We've also emailed this quote, with the PDF attached, to{" "}
              <strong className="font-bold">{emailedTo}</strong>. Give it a minute or two, and
              check your spam folder if it doesn't arrive.
            </span>
          </p>
        ) : null}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Button asChild className="h-12 text-base font-bold sm:min-w-[240px]">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="quote-download-pdf-button"
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Download PDF quote
            </a>
          </Button>
          <Button asChild variant="outline" className="h-12 bg-white">
            <Link
              to={`/verify-quote?ref=${encodeURIComponent(result.quote_ref)}`}
              data-testid="quote-verify-link"
            >
              <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
              Verify this quote
            </Link>
          </Button>
        </div>
      </div>

      <div className="tt-card p-5 sm:p-6" data-testid="quote-next-steps">
        <h3 className="text-base font-bold text-foreground">What would you like to do next?</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We already have your details — this just tells us how you'd like us to follow up.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {NEXT_STEPS.map((step) => {
            const Icon = step.icon;
            const active = choice === step.choice;
            return (
              <button
                key={step.choice}
                type="button"
                data-testid={step.testId}
                onClick={() => pickNextStep(step.choice)}
                disabled={savingChoice !== null}
                aria-pressed={active}
                className={`rounded-lg border px-4 py-4 text-left outline-none transition-[border-color,background-color] duration-150 focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-60 ${
                  active
                    ? "border-primary bg-[color:var(--tt-teal-mist)]"
                    : "border-input bg-white hover:border-[color:var(--tt-teal-line)] hover:bg-muted"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${active ? "text-primary" : "text-muted-foreground"}`}
                  aria-hidden="true"
                />
                <span className="mt-2.5 block text-sm font-bold text-foreground">
                  {step.label}
                </span>
                <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                  {active ? "Noted — thank you" : step.hint}
                </span>
              </button>
            );
          })}
        </div>
        {choice === "email_quote" ? (
          <p
            data-testid="quote-email-preference-note"
            className="mt-4 rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] px-4 py-3 text-[13px] leading-relaxed text-[color:var(--tt-teal-deep)]"
          >
            {emailed
              ? `Noted. Your quote has already gone to ${emailedTo} — we'll send it again if it hasn't arrived.`
              : "Noted against your quote so our team can send it across. Use the Download button above if you'd like your copy right now."}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Something not quite right?{" "}
          <a href="mailto:info@t-tite.co.za" className="tt-link">
            Email us
          </a>{" "}
          or call{" "}
          <a href="tel:+27817882993" className="tt-link">
            081 788 2993
          </a>
          .
        </p>
        <Button
          type="button"
          variant="ghost"
          onClick={onStartOver}
          data-testid="quote-start-over-button"
          className="self-start text-sm font-semibold text-primary sm:self-auto"
        >
          Price another service
        </Button>
      </div>
    </div>
  );
};
